/*---------- Deps, Hooks & CSS ----------*/
import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import styles from "./GuestBadgesStatusManager.module.css"

/*---------- Services ----------*/
import * as progressService from '../../../services/progressService'
import * as clientEventService from '../../../services/clientEventService'

/*---------- Components ----------*/
import Card from "../../../components/Home/StatusSubpages/Card"
import LoadingSpinner from '../../../components/Miscellaneous/LoadingSpinner'
import ProgressSteps from '../../../components/Miscellaneous/ProgressSteps'

/*---------- Images ----------*/
import ERSPropertyGuestMapping from "../../../components/Home/StatusSubpages/GuestBadgesImages/BadgeToERSPropertyGuestMapping.png"
import RegistrantToGuestMapping from "../../../components/Home/StatusSubpages/GuestBadgesImages/RegistrantToGuestMapping.png"
import BadgeTemplateStyling from "../../../components/Home/StatusSubpages/GuestBadgesImages/GuestBadgeTemplateStyling.png"
import TemplateFieldOrdering from "../../../components/Home/StatusSubpages/GuestBadgesImages/GuestBadgeTemplateFieldOrdering.png"

const GuestBadgesStatusManager = () => {

    const location = useLocation()
    const passedState = location.state
    const [clientEventInfo, setClientEventInfo] = useState({})
    const clientEventInfoRef = useRef(null);
    const [changeMade, setChangeMade] = useState(false)
    if (clientEventInfoRef === null || clientEventInfoRef.current === null) {
        if (!passedState || !passedState.clientName) {
            clientEventInfoRef.current = clientEventService.getClientEvent();
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            clientEventInfoRef.current = passedData
        }
    }
    useEffect(() => {
        // run events fetch every x seconds, if its too fast, it won't run - gets stuck
        const interval = setInterval(() => checkClientEvent(), (120 * 1000))
        return () => clearInterval(interval)
    }, [])
    const checkClientEvent = () => {
        // Update clientevent with every render when its value has changed.
        let savedClientEvent = clientEventService.getClientEvent()
        if (clientEventInfoRef.current && clientEventInfoRef.current.eventId !== undefined &&
            savedClientEvent && savedClientEvent.eventId !== undefined &&
            savedClientEvent.eventId !== clientEventInfoRef.current.eventId) {
            setClientEventInfo(savedClientEvent)
            clientEventInfoRef.current = savedClientEvent;
            setChangeMade(true)
        }
    }
    useEffect(() => {
        if (changeMade && clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined
            && clientEventInfo.eventId !== undefined && clientEventInfo.eventName !== undefined) {
            setChangeMade(false)
            fetchData()
        }

    }, [clientEventInfo])    
    /*---------- State ----------*/
    const [client, setClient] = useState(
        clientEventInfo.client
            ? clientEventInfo.client
            : clientEventInfo.clientId
                ? clientEventInfo.clientId
                : ""
    ) 
    const [event, setEvent] = useState(
        clientEventInfo.event
            ? clientEventInfo.event
            : clientEventInfo.eventId
                ? clientEventInfo.eventId
                : ""
    ) 
    const [clientEventObject, setClientEventObject] = useState({})
    const [loaded, setLoaded] = useState(false)

    // Required Setup
    const [ersPropertyGuestMappingProgress, setERSPropertyGuestMappingProgress] = useState("Loading...")
    const [registrantToGuestMappingProgress, setRegistrantToGuestMappingProgress] = useState("Loading...")
    const [templateStylingProgress, setTemplateStylingProgress] = useState("Loading...")


    // Optional Setup
    const [templateFieldOrderingProgress, setTemplateFieldOrderingProgress] = useState("Loading...")

    // Progress Steps
    const [requiredCompletionCount, setRequiredCompletionCount] = useState(1)
    const [optionalCompletionCount, setOptionalCompletionCount] = useState(1)

    /*---------- Constants ----------*/
    // General
    const clientName = clientEventInfo && clientEventInfo.clientName
        ? clientEventInfo.clientName
        : ""

    const eventName = clientEventInfo && clientEventInfo.eventName
            ? clientEventInfo.eventName
            : ""
            
    // curently does nothing, do we need it?
    const ERSName = clientEventInfo && clientEventInfo.eventRegistrationSystem !== undefined
        ? clientEventInfo.eventRegistrationSystem
        : "ERS"

    const clientId = clientEventInfo && clientEventInfo.clientId
        ? clientEventInfo.clientId
        : ""

    const eventId = clientEventInfo &&  clientEventInfo.eventId
            ? clientEventInfo.eventId
            : ""

    // Progress Steps 
    const requiredSteps = [
        { label: 'Badge To ERS Property Guest Mapping', step: 1 },
        { label: 'Registrant To Guest Mapping', step: 2 },
        { label: 'Template Styling - Guest Badge', step: 3 },
    ]

    const optionalSteps = [
        { label: 'Guest Badge Template Field Ordering', step: 1 },
    ]

    /*---------- Functions ----------*/
    // Step Calculations
    const checkRequiredCompletion = () => {
        if (ersPropertyGuestMappingProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (registrantToGuestMappingProgress === "Success") setRequiredCompletionCount(count => count + 1)
        if (templateStylingProgress === "Success") setRequiredCompletionCount(count => count + 1)
    }

    const checkOptionalCompletion = () => {
        if (templateFieldOrderingProgress === "Success") setOptionalCompletionCount(count => count + 1)
    }

    const checkLoaded = () => {
        if (ersPropertyGuestMappingProgress !== "Loading..."
            && registrantToGuestMappingProgress !== "Loading..."
            && templateStylingProgress !== "Loading..."
            && templateFieldOrderingProgress !== "Loading..."
        )
            setLoaded(true)
    }
    const fetchData = async () => {
        const clientEventObj = {
            ClientId: clientId,
            EventId: eventId,
        }
        setClientEventObject(clientEventObj)

        const [
            ersPropertyGuestMappingProgress,
            registrantToGuestMappingProgress,
            templateStylingProgress,
            templateFieldOrderingProgress,
        ] = await Promise.all([
            progressService.getBadgeToERSPropertyGuestMappingProgress(clientEventObj),
            progressService.getRegistrantToGuestMappingProgress(clientEventObj),
            progressService.getGuestBadgeTemplateStylingProgress(clientEventObj),
            progressService.getGuestBadgeTemplateFieldOrderingProgress(clientEventObj),
        ])
        setERSPropertyGuestMappingProgress(ersPropertyGuestMappingProgress)
        setRegistrantToGuestMappingProgress(registrantToGuestMappingProgress)
        setTemplateStylingProgress(templateStylingProgress)
        setTemplateFieldOrderingProgress(templateFieldOrderingProgress)
    }
    useEffect(() => {
        if (clientEventInfo && clientEventInfo.clientId !== undefined && clientEventInfo.clientName !== undefined) {
            fetchData()
        }
    }, [clientEventInfo])
    useEffect(() => {
        // store into local object
        if (!passedState || !passedState.clientName) {
            //let localclientevent = clientEventService.getClientEvent()
            if (clientEventInfoRef.current) {
                setClientEventInfo(clientEventInfoRef.current)
            }
        }
        else {
            let passedData = {
                clientName: passedState.clientName,
                clientId: passedState.clientId,
                eventName: passedState.eventName,
                eventId: passedState.eventId,
            }
            setClientEventInfo(passedData)
            clientEventInfoRef.current = passedData;
        }
    }, [])

    useEffect(() => {
        checkLoaded()
        checkRequiredCompletion()
        checkOptionalCompletion()
    }, [templateFieldOrderingProgress])
    
    return (
        <>
        {
                loaded ?                
                    <div id={styles.statusSubpage}>
                        <div className={styles.requiredSetupHeader}>
                            <h2 className={styles.requiredSetupHeading}>Required Setup</h2>
                            <ProgressSteps
                                steps={requiredSteps}
                                activeStep={requiredCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={RegistrantToGuestMapping}
                                header="Registrant To Guest Property Mapping"
                                content={`Map registrant field to guest field from ${ERSName} for ${eventName} badges`}
                                styles={styles}
                                linkName={`/RegistrantToGuestMapping`}
                                linkState={clientEventInfo}
                                disabledBool={false} // hardcoded as it's the first required step of an event --- can't be disabled
                            />
                            <Card
                                dataImage={ERSPropertyGuestMapping}
                                header="ERS Property Guest Mapping"
                                content={`Map registrant fields to guest fields from ${ERSName} for ${eventName} badges`}
                                styles={styles}
                                linkName={`/BadgeToERSPropertyGuestMapping`}
                                linkState={clientEventInfo}
                                disabledBool={ersPropertyGuestMappingProgress !== "Success" ? true : false} 
                            />
                            <Card
                                dataImage={BadgeTemplateStyling}
                                header="Guest Template Styling"
                                content={`Create styling rules & test printing for guest`}
                                styles={styles}
                                linkName={`/GuestBadgeTemplateStyling`}
                                linkState={clientEventInfo}
                                disabledBool={templateStylingProgress !== "Success" ? true : false}
                            />
                        </div>

                        <div className={styles.optionalSetupHeader}>
                            <h2 className={styles.optionalSetupHeading}>Optional Setup</h2>
                            <ProgressSteps
                                steps={optionalSteps}
                                activeStep={optionalCompletionCount}
                            />
                        </div>

                        <div className={styles.container}>
                            <Card
                                dataImage={TemplateFieldOrdering}
                                header="Guest Field Ordering"
                                content={`Arrange mapped fields in order you want them to be presented on your badges`}
                                styles={styles}
                                linkName={`/GuestBadgeTemplateFieldOrdering`}
                                linkState={clientEventInfo}
                                disabledBool={templateFieldOrderingProgress !== "Success" ? true : false}
                            />
                        </div>
                    </div>
            :
                <LoadingSpinner />
        }
        </>
    );
}

export default GuestBadgesStatusManager;